/**
 * Secure state synchronization between React and non-React contexts
 * Used to expose minimal state to request interceptors
 */

// Minimal state shape exposed to request interceptors
// Uses the global SiteISOAPI.SiteListItem type
export interface ExposedState {
  currentSite?: SiteAPI.SiteListItem;
}

// Private state storage
let exposedState: ExposedState = {};

// Listeners for state changes
const listeners = new Set<(state: ExposedState) => void>();

/**
 * Update the exposed state
 * Only exposes necessary fields for request interceptors
 */
export function exposeState(state: ExposedState): void {
  // Validate state shape before exposing
  const validated: ExposedState = {
    currentSite: state.currentSite,
  };

  exposedState = validated;

  // Notify listeners
  listeners.forEach((listener) => {
    try {
      listener(validated);
    } catch (e) {
      console.error('State sync listener error:', e);
    }
  });
}

/**
 * Get current exposed state
 * Safe to call from non-React contexts (request interceptors)
 */
export function getExposedState(): Readonly<ExposedState> {
  const state = Object.freeze({ ...exposedState });
  return state;
}

/**
 * Subscribe to state changes
 * Returns unsubscribe function
 */
export function subscribeToState(
  callback: (state: ExposedState) => void,
): () => void {
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
}

/**
 * Clear exposed state (e.g., on logout)
 */
export function clearExposedState(): void {
  exposedState = {};
  listeners.forEach((listener) => {
    listener({});
  });
}
